/**
 * Compact ids for interest scopes and group tokens (§3.8, ADR-0040): a UUID as 22 base64url characters, so
 * `t:<id>`, `i:<id>` and `p:<id>` cost ≈ 24–26 bytes each. Pure (no Buffer), isomorphic.
 */
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const INDEX = new Map([...ALPHABET].map((c, i) => [c, i]));
const HEX_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const COMPACT = /^[A-Za-z0-9_-]{22}$/;

function uuidBytes(uuid: string): number[] {
  const hex = uuid.replace(/-/g, '');
  const out: number[] = [];
  for (let i = 0; i < 32; i += 2) out.push(parseInt(hex.slice(i, i + 2), 16));
  return out;
}

/** UUID → 22-char base64url (no padding). */
export function compactId(uuid: string): string {
  if (!HEX_UUID.test(uuid)) throw new Error(`not a uuid: ${uuid}`);
  const b = uuidBytes(uuid);
  let s = '';
  for (let i = 0; i < 16; i += 3) {
    const n = ((b[i] ?? 0) << 16) | ((b[i + 1] ?? 0) << 8) | (b[i + 2] ?? 0);
    s += ALPHABET[(n >> 18) & 63]! + ALPHABET[(n >> 12) & 63]! + ALPHABET[(n >> 6) & 63]! + ALPHABET[n & 63]!;
  }
  return s.slice(0, 22);
}

/** 22-char base64url → UUID. */
export function expandId(compact: string): string {
  if (!COMPACT.test(compact)) throw new Error(`not a compact id: ${compact}`);
  const bytes: number[] = [];
  let acc = 0;
  let bits = 0;
  for (const c of compact) {
    acc = (acc << 6) | (INDEX.get(c) ?? 0);
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((acc >> bits) & 0xff);
    }
  }
  const hex = bytes
    .slice(0, 16)
    .map((x) => x.toString(16).padStart(2, '0'))
    .join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Group token prefixes that carry an id (§2.4). */
const ID_TOKEN = /^([tu]):(.+)$/;

/** `t:<uuid>` → `t:<compact>`; `w`, `m`, `a` unchanged. */
export function compactToken(token: string): string {
  const m = ID_TOKEN.exec(token);
  return m && HEX_UUID.test(m[2]!) ? `${m[1]!}:${compactId(m[2]!)}` : token;
}

/** `t:<compact>` → `t:<uuid>`. */
export function expandToken(token: string): string {
  const m = ID_TOKEN.exec(token);
  return m && COMPACT.test(m[2]!) ? `${m[1]!}:${expandId(m[2]!)}` : token;
}

/**
 * Interest scope kinds: an issue's, a project's or (W4 step 0, initiatives-core) an initiative's lazy children, or an
 * archive scope (§3.9).
 */
export type ScopeKind = 'i' | 'p' | 'n' | 'a';

export function scopeKey(kind: 'i' | 'p' | 'n', id: string): string {
  return `${kind}:${compactId(id)}`;
}
